/*
 * Changes: the home telling the pharmacy what the GP or the hospital changed.
 *
 * The home chooses the resident, sees their medicines, picks the one that changed — or says it is a
 * new one — and says what happened in their own words. It changes nothing on the chart: the pharmacy
 * makes the change on the resident's page and approves it. Until then the round shows the report
 * against the medicine, so whoever is giving it knows.
 *
 * The list stays as the trail: what was reported, by whom, and what the pharmacy did about it.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import type { Resident, User } from './api.ts';
import { moment } from './days.ts';
import type { Medicine, Report } from './medicine-types.ts';
import { Overlay } from './overlay.tsx';

const WHAT: Array<[Report['what'], string, string]> = [
  ['stopped', 'Stopped', 'The GP or hospital has stopped it'],
  ['dose', 'Dose changed', 'More, less, or a different strength'],
  ['times', 'Times changed', 'Different times of day, or different days'],
];

const REPORT_SOURCES: Array<[string, string]> = [
  ['gp', 'GP'],
  ['hospital', 'Hospital'],
  ['clinic', 'Clinic or specialist'],
  ['other', 'Someone else'],
];

export function ChangesScreen({
  homeId, user, onOpenResident,
}: { homeId: string; user: User; onOpenResident?: (residentId: string) => void }) {
  const [reports, setReports] = useState<Report[] | null>(null);
  const [show, setShow] = useState<'open' | 'all'>('open');
  const [reporting, setReporting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const isPharmacy = user.roles.includes('pharmacy') && user.side === 'pharmacy';
  const mayReport = ['meds', 'manage', 'nurse', 'check'].some((role) => user.roles.includes(role));

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ reports: Report[] }>(`/api/homes/${homeId}/reports?show=${show}`);
      setReports(data.reports);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the changes.');
    }
  }, [homeId, show]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section>
      <div className="head">
        <div>
          <h1>Changes</h1>
          <p className="muted">
            {isPharmacy
              ? 'What the home has told the pharmacy. Make each change on the resident\'s page, then approve it.'
              : 'Tell the pharmacy when the GP or the hospital changes a medicine. The pharmacy updates the chart.'}
          </p>
        </div>
        {mayReport && !isPharmacy ? (
          <button type="button" onClick={() => setReporting(true)}>Report a change</button>
        ) : null}
      </div>

      <nav className="tabs">
        <button type="button" className={show === 'open' ? 'on' : ''} onClick={() => setShow('open')}>Waiting for the pharmacy</button>
        <button type="button" className={show === 'all' ? 'on' : ''} onClick={() => setShow('all')}>Everything</button>
      </nav>

      {message ? <p className="notice" role="status">{message}</p> : null}
      {error ? <p className="error" role="alert">{error}</p> : null}
      {reports === null ? <p className="muted">Loading…</p> : null}
      {reports && !reports.length ? (
        <p className="muted">{show === 'open' ? 'Nothing is waiting for the pharmacy.' : 'No changes have been reported yet.'}</p>
      ) : null}

      <ul className="list">
        {(reports ?? []).map((report) => (
          <li key={report.id}>
            <div className="row static column report-line">
              <div className="row-line">
                <span>
                  <span className={`chip ${report.status === 'open' ? 'warn' : 'done'}`}>
                    {report.status === 'open' ? 'Waiting' : 'Done'}
                  </span>{' '}
                  <span className="row-main">{report.residentName}</span>
                  <span className="muted"> · {report.whatLabel}{report.medicineName ? `: ${report.medicineName}` : ''}</span>
                </span>
                <span className="muted small">{moment(report.reportedAt)}</span>
              </div>
              <p className="small">"{report.detail}"</p>
              <p className="muted small">
                Reported by {report.reportedBy}
                {report.sourceDetail ? ` · ${report.sourceDetail}` : ''}
              </p>
              {report.status === 'done' ? (
                <p className="small">
                  <strong>{report.doneNote}</strong>
                  <span className="muted"> — {report.doneBy}{report.doneAt ? `, ${moment(report.doneAt)}` : ''}</span>
                </p>
              ) : null}
              {isPharmacy && report.status === 'open' && onOpenResident ? (
                <div className="row-actions">
                  <button type="button" onClick={() => onOpenResident(report.residentId)}>Go to {report.residentName}</button>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {reporting ? (
        <ReportChange
          homeId={homeId}
          onClose={() => setReporting(false)}
          onDone={() => {
            setReporting(false);
            setMessage('Sent to the pharmacy. The round shows it against the medicine until the chart is updated.');
            void load();
          }}
        />
      ) : null}
    </section>
  );
}

/* Choosing the resident, then the medicine, then saying what changed. */
function ReportChange({ homeId, onClose, onDone }: { homeId: string; onClose: () => void; onDone: () => void }) {
  const [residents, setResidents] = useState<Resident[]>([]);
  const [residentId, setResidentId] = useState('');
  const [medicines, setMedicines] = useState<Medicine[] | null>(null);
  // A medicine's id, 'new' for a new medicine, or 'other' for anything else.
  const [about, setAbout] = useState('');
  const [what, setWhat] = useState<Report['what'] | ''>('');
  const [detail, setDetail] = useState('');
  const [source, setSource] = useState('gp');
  const [sourceDetail, setSourceDetail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ residents: Resident[] }>(`/api/homes/${homeId}/residents`)
      .then((data) => setResidents(data.residents.filter((r) => !r.status || r.status === 'here' || r.status === 'hospital' || r.status === 'away')))
      .catch(() => setResidents([]));
  }, [homeId]);

  useEffect(() => {
    setMedicines(null);
    setAbout('');
    setWhat('');
    if (!residentId) return;
    api
      .get<{ medicines: Medicine[] }>(`/api/residents/${residentId}/medicines`)
      .then((data) => setMedicines(data.medicines))
      .catch(() => setMedicines([]));
  }, [residentId]);

  const chosen = medicines?.find((m) => m.id === about) ?? null;
  const kind: Report['what'] | '' = about === 'new' ? 'new' : about === 'other' ? 'other' : what;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!kind) {
      setError('Say what has changed.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.post(`/api/homes/${homeId}/reports`, {
        residentId,
        medicineId: chosen?.id,
        what: kind,
        detail,
        source,
        sourceDetail,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not send.');
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>Report a change to the pharmacy</h2>
        <p className="muted">
          This tells the pharmacy. It does not change the chart — the pharmacy does that, usually the same day
          between 9 and 6. Until then the round shows your report against the medicine.
        </p>

        <label htmlFor="r-resident">Resident</label>
        <select id="r-resident" required value={residentId} onChange={(e) => setResidentId(e.target.value)}>
          <option value="">Choose a resident</option>
          {residents.map((r) => (
            <option key={r.id} value={r.id}>
              {r.title ? `${r.title} ` : ''}{r.firstName} {r.lastName}{r.room ? ` · Room ${r.room}` : ''}
            </option>
          ))}
        </select>

        {residentId ? (
          <fieldset>
            <legend>Which medicine</legend>
            {medicines === null ? <p className="muted small">Loading their medicines…</p> : null}
            {(medicines ?? []).map((m) => (
              <label className="check" key={m.id} htmlFor={`r-med-${m.id}`}>
                <input id={`r-med-${m.id}`} type="radio" name="r-about" checked={about === m.id} onChange={() => setAbout(m.id)} />
                <span><strong>{m.name}</strong><span className="muted small"> {m.directions}</span></span>
              </label>
            ))}
            <label className="check" htmlFor="r-med-new">
              <input id="r-med-new" type="radio" name="r-about" checked={about === 'new'} onChange={() => setAbout('new')} />
              <span><strong>A new medicine</strong><span className="muted small"> not on their chart yet</span></span>
            </label>
            <label className="check" htmlFor="r-med-other">
              <input id="r-med-other" type="radio" name="r-about" checked={about === 'other'} onChange={() => setAbout('other')} />
              <span><strong>Something else</strong><span className="muted small"> a review, an allergy, anything not about one medicine</span></span>
            </label>
          </fieldset>
        ) : null}

        {chosen ? (
          <fieldset>
            <legend>What has changed</legend>
            {WHAT.map(([id, label, hint]) => (
              <label className="check" key={id} htmlFor={`r-what-${id}`}>
                <input id={`r-what-${id}`} type="radio" name="r-what" checked={what === id} onChange={() => setWhat(id)} />
                <span><strong>{label}</strong><span className="muted small"> {hint}</span></span>
              </label>
            ))}
          </fieldset>
        ) : null}

        {about ? (
          <>
            <label htmlFor="r-detail">In your own words</label>
            <textarea
              id="r-detail"
              required
              minLength={3}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder={
                about === 'new'
                  ? 'Amoxicillin 500mg, ONE three times a day for 7 days, starting today'
                  : 'GP halved the dose to ONE tablet in the morning'
              }
            />

            <label htmlFor="r-source">Who changed it</label>
            <select id="r-source" value={source} onChange={(e) => setSource(e.target.value)}>
              {REPORT_SOURCES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>

            <label htmlFor="r-source-detail">Their name, and when</label>
            <input id="r-source-detail" value={sourceDetail} onChange={(e) => setSourceDetail(e.target.value)}
              placeholder="Dr Shah, visit this morning at 10:05" />
          </>
        ) : null}

        {error ? <p className="error" role="alert">{error}</p> : null}
        <div className="row-actions">
          <button type="submit" disabled={busy || !kind || detail.trim().length < 3}>
            {busy ? 'Sending…' : 'Send to the pharmacy'}
          </button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}
